import { useState } from "react";
import {
  CalendarDays,
  Check,
  CircleHelp,
  ExternalLink,
  House,
  Mail,
  PencilLine,
  Send,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";
import { COTTAGES } from "../../data/cottages";
import { Reveal } from "./Reveal";
import { SectionHead } from "./Cottages";
import { Divider } from "./Ornaments";

const EMPTY = { name: "", email: "", cottage: "unsure", dateFrom: "", dateTo: "", message: "" };
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

const validate = (v) => {
  const e = {};
  if (!v.name.trim()) e.name = "nameRequired";
  if (!v.email.trim()) e.email = "emailRequired";
  else if (!EMAIL_RE.test(v.email.trim())) e.email = "emailInvalid";
  if (!v.message.trim()) e.message = "messageRequired";
  return e;
};

const Field = ({ id, label, error, optional, icon: Icon, children }) => {
  const { t } = useLang();
  return (
    <div className={`field ${error ? "has-error" : ""}`}>
      <label htmlFor={id}>
        {label}
        {optional && <span className="field__opt"> ({t.contact.optional})</span>}
      </label>
      <div className="field__control">
        {Icon && <Icon className="field__control-icon" size={20} strokeWidth={1.6} aria-hidden="true" />}
        {children}
      </div>
      {error && (
        <p id={`${id}-error`} className="field__error" role="alert" data-testid={`${id}-error`}>
          {t.contact.errors[error]}
        </p>
      )}
    </div>
  );
};

const Aside = () => {
  const { t, lang } = useLang();
  return (
    <Reveal className="contact__aside" delay={120}>
      <p className="contact__nobooking" data-testid="contact-no-booking-note">{t.contact.noBooking}</p>
      <h4>{t.contact.directTitle}</h4>
      <p>{t.contact.directText}</p>
      <ul className="contact__links">
        {COTTAGES.map((c) => (
          <li key={c.key}>
            <a href={c.booking[lang]} target="_blank" rel="noopener noreferrer" data-testid={`contact-booking-link-${c.key}`}>
              {c.name}
              <ExternalLink size={14} strokeWidth={1.6} aria-hidden="true" />
              <span className="sr-only">({t.cottages.newTab})</span>
            </a>
          </li>
        ))}
      </ul>
      <p className="contact__demo" data-testid="contact-demo-note">
        <ShieldCheck size={15} strokeWidth={1.5} aria-hidden="true" />
        {t.contact.demoNote}
      </p>
    </Reveal>
  );
};

const Success = ({ onAgain }) => {
  const { t } = useLang();
  return (
    <div className="contact__success" role="status" tabIndex={-1} data-testid="contact-success-panel">
      <p className="script contact__success-script" aria-hidden="true">{t.contact.successScript}</p>
      <Divider />
      <p className="contact__success-text" data-testid="contact-success-text">{t.contact.success}</p>
      <button type="button" className="btn btn--outline" onClick={onAgain} data-testid="contact-send-another-button">
        {t.contact.again}
      </button>
    </div>
  );
};

const CottageChoiceIcon = ({ choice, active }) => {
  if (choice === "both") return <House size={18} strokeWidth={1.5} aria-hidden="true" />;
  if (choice === "unsure") {
    return active
      ? <Check size={18} strokeWidth={1.8} aria-hidden="true" />
      : <CircleHelp size={18} strokeWidth={1.5} aria-hidden="true" />;
  }
  return <House size={18} strokeWidth={1.5} aria-hidden="true" />;
};

export const Contact = () => {
  const { t, lang } = useLang();
  const c = t.contact;
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [tried, setTried] = useState(false);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  const set = (k) => (e) => {
    const next = { ...values, [k]: e.target.value };
    setValues(next);
    if (tried) setErrors(validate(next));
  };

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(values);
    setTried(true);
    setErrors(errs);
    const first = Object.keys(errs)[0];
    if (first) {
      document.getElementById(`contact-${first}`)?.focus();
      return;
    }
    setSendFailed(false);
    setSending(true);
    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...values, lang }),
      });
      if (!response.ok) throw new Error("Contact delivery failed");
      setSent(true);
    } catch (error) {
      setSendFailed(true);
    } finally {
      setSending(false);
    }
  };

  const again = () => {
    setValues(EMPTY);
    setErrors({});
    setTried(false);
    setSent(false);
    setSendFailed(false);
  };

  const aria = (k) => ({
    "aria-invalid": !!errors[k],
    "aria-describedby": errors[k] ? `contact-${k}-error` : undefined,
  });

  return (
    <section id="kontakt" className="section contact" data-testid="section-kontakt">
      <div className="wrap">
        <SectionHead eyebrow={c.eyebrow} title={c.title} lead={c.lead} testId="contact-heading" center />
        <div className="contact__grid">
          <Reveal className="contact__card">
            {sent ? (
              <Success onAgain={again} />
            ) : (
              <form noValidate onSubmit={submit} data-testid="contact-form">
                <div className="form-row">
                  <Field id="contact-name" label={c.name} error={errors.name} icon={UserRound}>
                    <input
                      id="contact-name"
                      type="text"
                      autoComplete="name"
                      placeholder={c.namePh}
                      value={values.name}
                      onChange={set("name")}
                      {...aria("name")}
                      data-testid="contact-name-input"
                    />
                  </Field>
                  <Field id="contact-email" label={c.email} error={errors.email} icon={Mail}>
                    <input
                      id="contact-email"
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder={c.emailPh}
                      value={values.email}
                      onChange={set("email")}
                      {...aria("email")}
                      data-testid="contact-email-input"
                    />
                  </Field>
                </div>

                <fieldset className="field choice">
                  <legend>{c.cottage}</legend>
                  <div className="choice__options">
                    {Object.entries(c.options).map(([k, label]) => {
                      const active = values.cottage === k;
                      return (
                        <label key={k} className={`choice__pill ${active ? "is-checked" : ""}`} data-testid={`contact-cottage-${k}`}>
                          <input type="radio" name="cottage" value={k} checked={active} onChange={set("cottage")} />
                          <CottageChoiceIcon choice={k} active={active} />
                          <span>{label}</span>
                        </label>
                      );
                    })}
                  </div>
                </fieldset>

                <div className="field date-field">
                  <span className="field__label field__label--icon">
                    <CalendarDays size={20} strokeWidth={1.6} aria-hidden="true" />
                    <span>
                      {c.dates}
                      <span className="field__opt"> ({c.optional})</span>
                    </span>
                  </span>
                  <div className="date-range" role="group" aria-label={c.dates}>
                    <label className="date-range__field" htmlFor="contact-date-from">
                      <span>{c.dateFrom}</span>
                      <input
                        id="contact-date-from"
                        type="date"
                        value={values.dateFrom}
                        onChange={set("dateFrom")}
                        data-testid="contact-date-from-input"
                      />
                    </label>
                    <span className="date-range__arrow" aria-hidden="true">→</span>
                    <label className="date-range__field" htmlFor="contact-date-to">
                      <span>{c.dateTo}</span>
                      <input
                        id="contact-date-to"
                        type="date"
                        min={values.dateFrom || undefined}
                        value={values.dateTo}
                        onChange={set("dateTo")}
                        data-testid="contact-date-to-input"
                      />
                    </label>
                  </div>
                </div>

                <Field id="contact-message" label={c.message} error={errors.message} icon={PencilLine}>
                  <textarea
                    id="contact-message"
                    rows={5}
                    placeholder={c.messagePh}
                    value={values.message}
                    onChange={set("message")}
                    {...aria("message")}
                    data-testid="contact-message-input"
                  />
                </Field>

                {sendFailed && <p className="contact__send-error" role="alert" data-testid="contact-send-error">{c.sendFailed}</p>}
                <div className="form-foot">
                  <button type="submit" disabled={sending} className="btn btn--primary contact__submit" data-testid="contact-submit-button">
                    <Send size={18} strokeWidth={1.7} aria-hidden="true" />
                    {sending ? c.sending : c.submit}
                  </button>
                  <span className="form-foot__divider" aria-hidden="true" />
                  <p className="form-foot__note">
                    <CalendarDays size={17} strokeWidth={1.5} aria-hidden="true" />
                    {c.noBooking}
                  </p>
                </div>
              </form>
            )}
          </Reveal>
          <Aside />
        </div>
      </div>
    </section>
  );
};
